<template>
    <div class="col-md-12">
        <ejs-grid id="categoryGrid"
                  ref="categoryGrid"
                  :dataSource="gridData"
                  :allowSorting="true"
                  :sortSettings="sortSettings"
                  :toolbar="toolbar"
                  :allowGrouping="false"
                  :allowPaging="true"
                  :pageSettings="pageSettings"
                  :showColumnChooser="false"
                  :allowExcelExport="true"
                  :dataStateChange="gridDataStateChanged"
                  :queryCellInfo="customiseCell"
                  :toolbarClick="toolbarClicked"
                  :rowSelected="rowSelectionChanged"
                  :rowDeselected="rowSelectionChanged"
                  :headerCellInfo="headerCellInfoEvent"
                  :created="gridCreated"
                  :enableStickyHeader="true">
            <e-columns>
                <e-column field="name" headerText="Name"></e-column>
            </e-columns>
        </ejs-grid>

        <validation-observer v-slot="vo" slim>
            <b-modal size="md"
                     centered
                     :title="createOrEditTitle"
                     v-model="createEntryDialogShown"
                     v-on:ok.prevent="vo.handleSubmit(createOrEditEntry)">
                <b-form-row>
                    <validation-provider ref="categoryNameProvider"
                                         rules="required"
                                         name="Category Name"
                                         v-slot="vp"
                                         slim>
                        <b-form-group label="Category Name" label-for="currentGlossaryCategory.name" class="col-md-12">
                            <b-form-input ref="categoryName" name="categoryName" v-model="currentGlossaryCategory.name" autofocus />
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>

                <template #modal-footer>
                    <div class="w-100 mb-2 p-0">
                        <div class="float-right">
                            <b-button v-on:click="createOrEditEntry" variant="success" class="mr-1" data-toggle="tooltip" title="Enter">Save</b-button>
                            <b-button v-on:click="closeAddEntry" variant="danger" class="mr-2" data-toggle="tooltip" title="Esc">Cancel</b-button>
                        </div>
                    </div>
                </template>
            </b-modal>
        </validation-observer>        

        <validation-observer v-slot="vo" slim>
            <b-modal size="md"
                     centered
                     title="Delete Glossary Category Entry"
                     v-model="deleteEntryDialogShown"
                     v-on:ok.prevent="vo.handleSubmit(createOrEditEntry)">

                <b-form-row class="mb-2">
                    Are you sure you wish to DELETE the Glossary Entry Category '{{ currentGlossaryCategory.name }}'?
                </b-form-row>
                
                <b-form-row v-if="currentCategoryCount > 0">
                    '{{ currentGlossaryCategory.name }}' contains {{ currentCategoryCount }} existing glossary entries.
                    <br/>Do wish to assign them to another category?
                    <b-form-group label="Select New Category" label-for="glossaryCategory" class="col-md-8 mt-2">
                        <select2 name="glossaryCategory"
                                 v-model="newGlossaryEntryCategoryId"
                                 :options="allGlossaryCategoryOptions"
                                 in-b-modal>
                        </select2>
                    </b-form-group>
                </b-form-row>

                <template #modal-footer>
                    <div class="w-100 mb-2 p-0">
                        <div class="float-right">
                            <b-button v-on:click="deleteCategory" variant="danger" class="mr-1" data-toggle="tooltip" title="Enter">Yes, DELETE</b-button>
                            <b-button v-on:click="closeDeleteEntry" variant="secondary" class="mr-2" data-toggle="tooltip" title="Esc">Cancel</b-button>
                        </div>
                    </div>
                </template>
            </b-modal>
        </validation-observer>

    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { GridPlugin, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport } from '@syncfusion/ej2-vue-grids';
    import { DataManager } from "@syncfusion/ej2-data";
    import axios from 'axios';

    import Attachments from '../Attachments.vue';
    Vue.component('attachments', Attachments);
    Vue.use(Attachments);

    import VueCurrencyInput from 'vue-currency-input'
    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required, min_value, max_value, email } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('min_value', min_value);
    extend('max_value', max_value);
    extend('email', email);
    localize('en', en);

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import GridMixins from '../../mixins/GridMixins';
    import NumbersMixin from '../../mixins/NumbersMixin';
    import Select2 from '../Select2';

    Vue.use(BootstrapVue);
    Vue.use(GridPlugin)
    Vue.use(VueCurrencyInput);
    Vue.use(NumbersMixin);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);

    export default {
        name: 'GlossaryEntryCategoryGrid',
        mixins: [ErrorsMixin, DateTimeMixin, GridMixins, NumbersMixin],
        components: {
        },
        provide: {
            grid: [Resize, Sort, Group, Page, Toolbar, Search, ExcelExport]
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            canCreateGlossaryCategories: {
                type: Boolean,
                default: false
            },
            canModifyGlossaryCategories: {
                type: Boolean,
                default: false
            },
            canDeleteGlossaryCategories: {
                type: Boolean,
                default: false
            },
            initialGlossaryCategoryOptions: {
                type: Array,
                default: null
            },
        },
        data: function () {
            return {
                createEntryDialogShown: false,
                deleteEntryDialogShown: false,
                currentGlossaryCategory: {
                    glossaryEntryCategoryId: null,
                    name: null,
                },
                currentCategoryCount: 0,
                newGlossaryEntryCategoryId: null,

                pageSettings: { pageSize: 10, pageSizes: [5, 10, 25, 50] },

                initialized: false,

                sortSettings: { columns: [{ field: "name", direction: "Ascending" }] },
                gridDataState: {
                    filter: null,
                    search: null,
                    sorted: [{ name: "name", direction: "Ascending" }],
                    group: null,
                    skip: 0,
                    take: 10,
                    requiresCounts: true
                },
                gridData: { result: [], count: 0 },
                grid: null,

                //currentPayment: null,
                showErrors: false,
                currentErrors: null,

                columnFormats: {
                    'createdDateTimeUTC': {
                        type: 'datetime'
                    },
                },
                allGlossaryCategoryOptions: [],
            }
        },
        computed: {
            toolbar: function () {
                var toolbarItems = this.defaultEJ2DataGridToolbarItems({ grouping: false, export: true, columnchooser: false });
                toolbarItems =
                    toolbarItems.concat(
                        [
                            { text: "Create", tooltipText: "Create Category", prefixIcon: "fas fa-plus", id: "addCategory" },
                            { text: "Edit", tooltipText: "Edit Category", prefixIcon: "fas fa-edit", id: "editCategory" },
                            { text: "Delete", tooltipText: "Delete Category", prefixIcon: "fas fa-times", id: "deleteCategory" },
                        ]
                    );

                return toolbarItems;
            },
            createOrEditTitle: function () {
                if (this.currentGlossaryCategory.glossaryEntryCategoryId != null) { return "Edit Glossary Entry Category"; }
                else { return "Create Glossary Entry Category"; }
            }
        },
        methods: {
            customiseCell: function (args) {
                this.defaultEJ2DataGridQueryCellInfo(args, this.columnFormats);
            },
           
            gridDataStateChanged: function (args) {
                this.gridDataState.search = args.search;
                this.gridDataState.sorted = args.sorted;
                this.gridDataState.group = args.group;
                this.gridDataState.skip = args.skip;
                this.gridDataState.take = args.take;
                this.gridDataState.requiresCounts = args.requiresCounts;
                this.grid.showSpinner();
                this.loadCategories(this.gridDataState)
                    .then(result => {
                        if (result) {
                            this.gridData = result;
                        }
                        this.grid.hideSpinner();
                    });
            },
            toolbarClicked: function (args) {
                if (args.item.id === 'addCategory') {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'editCategory') {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'deleteCategory') {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'excelexport') {
                    console.log("export");
                    //to properly export all data (and not just the entries displayed on the current page) we have to load everything first
                    //we do this by taking the current grid state (which includes the current filter and search and sort settings)
                    var gridDataState = {
                        ...this.gridDataState
                    };
                    //but reset the skip and take to 0 which will return everything and unset the requiresCounts cause we are not interseted in the totals
                    gridDataState.requiresCounts = false;
                    this.grid.showSpinner();
                    this.loadCategories(gridDataState)
                        .then(result => {
                            //the result is either false if something failed during the axios request, or it will contain the list in the result property which is what we have to pass to the actual export
                            if (result) {
                                var query = this.grid.getDataModule().generateQuery(true);
                                var groupedData = new DataManager(result.result).executeLocal(query);
                                this.grid.excelExport({ dataSource: groupedData });
                            }
                            this.grid.hideSpinner();
                        });                
                } else {
                    this.defaultEJ2DataGridToolbarClickHandler(args, this.grid);
                }
            },
            handleAction: function (action, selectedRow) {
                if (action === 'addCategory') {
                    this.currentGlossaryCategory.glossaryEntryCategoryId = null;
                    this.currentGlossaryCategory.newGlossaryEntryCategoryId = null;
                    this.currentGlossaryCategory.name = null;
                    this.createEntryDialogShown = true;
                }
                else if (action === 'editCategory') {
                    if (selectedRow == null) { return; }
                    this.editCategory(selectedRow);
                }
                else if (action === 'deleteCategory') {
                    if (selectedRow == null) { return; }
                    this.confirmDeleteCategory(selectedRow);                    
                }
            },
            rowSelectionChanged: function (args) {
                if (args.name == 'rowSelected') {
                    this.updateToolbarItems(this.grid.getSelectedRecords()[0])
                } else {
                    this.updateToolbarItems(null)
                }
            },            
            updateToolbarItems: function (item = null) {
                this.setToolbarItemState('addCategory', (this.canCreateGlossaryCategories));
                this.setToolbarItemState('editCategory', (item != null && this.canModifyGlossaryCategories));
                this.setToolbarItemState('deleteCategory', (item != null && this.canDeleteGlossaryCategories));
            },
            setToolbarItemState: function (itemId, enabled) {
                this.grid.toolbarModule.enableItems([itemId], enabled);
            },

            loadCategories: function (gridDataState) {
                var url = this.$urlHelper.getApiUrl('ElectronicGlossaryCategory', 'listCategories');
                return axios
                    .post(url, JSON.stringify(gridDataState), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var gridData = response.data;
                        if (gridData && gridData.result) {
                            gridData.result = gridData.result.map((asr) => {
                                if (asr.createdDateTimeUTC) asr.createdDateTimeUTC = this.parseDateTime(asr.createdDateTimeUTC, true, true);
                                return asr;
                            });
                        }
                        return gridData;
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },
            loadCategoryList: function (excludedCategoryId) {
                var url = this.$urlHelper.getApiUrl('ElectronicGlossaryCategory', 'selectlist');
                console.log(url);

                axios
                    .get(url, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var entries = response.data;
                        entries.forEach(entry => {
                            if (entry.text != "" && entry.value != excludedCategoryId) {
                                this.allGlossaryCategoryOptions.push(entry);
                            }
                        });
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    });
            },

            createGlossaryCategory: function (name) {
                return {
                    id: this.newUuid(),
                    text: name
                }
            },
            editCategory: function (selectedRow) {
                this.currentGlossaryCategory.glossaryEntryCategoryId = selectedRow.glossaryEntryCategoryId;
                this.currentGlossaryCategory.newGlossaryEntryCategoryId = null;
                this.currentGlossaryCategory.name = selectedRow.name;                

                this.createEntryDialogShown = true;
            },
            createOrEditEntry: function () {
                if (!this.createEntryDialogShown) {
                    return;
                }

                var categoryName = this.currentGlossaryCategory.name.trim();
                if (categoryName == null || categoryName == '') {
                    return;
                }

                var url = this.$urlHelper.getApiUrl('ElectronicGlossaryCategory', 'createOrUpdate/');

                if (this.currentGlossaryCategory.glossaryEntryCategoryId) { url += this.currentGlossaryCategory.glossaryEntryCategoryId; }
                console.log(url);
                var newCategory = categoryName;
                axios
                    .post(url, JSON.stringify(newCategory), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    })
                    .finally(() => {
                        this.createEntryDialogShown = false;
                        this.currentGlossaryCategory.glossaryEntryCategoryId = null;
                        this.currentGlossaryCategory.newGlossaryEntryCategoryId = null;
                        this.currentGlossaryCategory.name = null;
                        this.currentCategoryCount = 0;
                        this.grid.refresh();
                    });

            },
            closeAddEntry: function () {
                this.createEntryDialogShown = false;
                this.currentGlossaryCategory.glossaryEntryCategoryId = null;
                this.currentGlossaryCategory.name = null;
                this.currentCategoryCount = 0;
            },

            confirmDeleteCategory: function (selectedRow) {
                this.grid.showSpinner();                

                this.currentGlossaryCategory.glossaryEntryCategoryId = selectedRow.glossaryEntryCategoryId;
                this.currentGlossaryCategory.name = selectedRow.name;
                this.newGlossaryEntryCategoryId = null;          

                var url = this.$urlHelper.getApiUrl('ElectronicGlossaryCategory', 'categoryCount/' + this.currentGlossaryCategory.glossaryEntryCategoryId);
                axios
                    .get(url)
                    .then(response => {
                        var count = response.data;
                        this.currentCategoryCount = count;
                        console.log(count);

                        if (this.currentCategoryCount > 0) {
                            this.allGlossaryCategoryOptions = [];

                            var emptyEntry = {
                                disabled: false,
                                text: "No Category",
                                id: "00000000-0000-0000-0000-000000000000",
                                value: "00000000-0000-0000-0000-000000000000",
                                selected: true,
                            };

                            this.allGlossaryCategoryOptions.push(emptyEntry);

                            this.loadCategoryList(selectedRow.glossaryEntryCategoryId);
                        }
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    })  
                    .finally(() => {
                        this.grid.hideSpinner();
                        this.deleteEntryDialogShown = true;
                    }); 
            },
            deleteCategory: function () {
                var url = this.$urlHelper.getApiUrl('ElectronicGlossaryCategory', 'delete/' + this.currentGlossaryCategory.glossaryEntryCategoryId);
                if (this.newGlossaryEntryCategoryId != null) {
                    url += '/' + this.newGlossaryEntryCategoryId
                }
                console.log(url);
                axios
                    .post(url)
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    })
                    .finally(() => {
                        this.deleteEntryDialogShown = false;
                        this.currentGlossaryCategory.glossaryEntryCategoryId = null;
                        this.currentGlossaryCategory.name = null;
                        this.currentCategoryCount = 0;
                        this.newGlossaryEntryCategoryId = null;
                        this.grid.refresh();
                    });
            },
            closeDeleteEntry: function () {
                this.deleteEntryDialogShown = false;
                this.currentGlossaryCategory.glossaryEntryCategoryId = null;
                this.currentGlossaryCategory.name = null;
                this.currentCategoryCount = 0;
                this.newGlossaryEntryCategoryId = null;
            },

            gridCreated: function () {
                Vue.nextTick(() => {
                    this.defaultEJ2DataGridCreatedHandler(this.grid);
                });
            },
        },
        mounted: function () {
            this.grid = this.$refs.categoryGrid.ej2Instances;
            this.setupGridPersistence(this.grid);
            //this.updateToolbarItems(this.grid);

            this.initialized = true;
            //setTimeout(() => {
                this.grid.refresh();
                this.updateToolbarItems();
            //}, 250);
        }
    }
</script>
<style scoped>
    div >>> .user-disabled {
        background-color: #F5C6CB !important;
    }

    div >>> .user-not-validated {
        background-color: #FFEEBA !important;
    }

    div >>> .user-locked {
        background-color: #F5C6CB !important;
    }
</style>